import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { v4 } from 'uuid'

import { addKeyDownListener, addMouseUpListener, removeKeyDownListener, removeMouseUpListener } from '@utils/listeners'
import { useWindowSize } from '@utils/hooks'

import { Sheet } from '@layouts/sheet'
import { Portal } from '../Portal'
import { ContainerWrappedComponent, ContainerDropDown } from './DropDown.styled'

export interface DropDownActions {
  display: boolean
  show: () => void
  hide: () => void
}

interface Props {
  position?: 'left' | 'right'
  disabled?: boolean
  contextMenu?: boolean
  containerWrappedComponentStyle?: React.CSSProperties
  dropDownWidth?: number
  WrappedComponent: (props: DropDownActions) => JSX.Element
  WrappedChildren: (props: DropDownActions) => JSX.Element
  disabledVerticalCorrection?: boolean
  defaultDropdownStyle?: React.CSSProperties
  onKeyDown?: (event: KeyboardEvent, actions: DropDownActions) => void
  onHide?: () => void
}

export const DropDown = (props: Props): JSX.Element => {
  const [displayDropDown, setDisplayDropDown] = useState<boolean>(false)

  const refComponent = useRef<string>(v4())
  const refWrappedComponent = useRef<HTMLDivElement | null>(null)
  const refDropDown = useRef<HTMLDivElement | null>(null)

  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({
    position: 'fixed',
    zIndex: 1000,
    ...props.defaultDropdownStyle,
  })

  const {
    onKeyDown,
    position,
    disabled,
    contextMenu,
    containerWrappedComponentStyle,
    WrappedComponent,
    WrappedChildren,
    dropDownWidth = 0,
    disabledVerticalCorrection,
    onHide,
  } = props

  const getPosition = useCallback((element: HTMLElement) => {
    const rect = element.getBoundingClientRect()
    return {
      top: rect.top,
      left: rect.left,
      height: rect.height,
      width: rect.width,
      bottom: rect.bottom,
      right: rect.right,
    }
  }, [])

  const updatePosition = useCallback(() => {
    if (!refWrappedComponent.current || !displayDropDown) return

    const rect = getPosition(refWrappedComponent.current)
    const style: React.CSSProperties = dropdownStyle

    if (!position) {
      if (window.innerWidth / 2 > rect.left) {
        style.left = rect.left
      } else {
        style.right = window.innerWidth - rect.right
      }
    } else if (position === 'left' && window.innerWidth - dropDownWidth > rect.left) {
      style.left = rect.left
    } else {
      style.right = window.innerWidth - rect.right
    }

    if (!disabledVerticalCorrection && rect.top > window.innerHeight / 2) {
      style.bottom = window.innerHeight - rect.top + 5
    } else {
      style.top = rect.bottom + 5
    }

    setDropdownStyle(style)
  }, [disabledVerticalCorrection, displayDropDown, dropDownWidth, getPosition, position, dropdownStyle])

  useEffect(() => {
    if (displayDropDown) {
      updatePosition()

      window.addEventListener('scroll', updatePosition, true)
      window.addEventListener('resize', updatePosition)

      return () => {
        window.removeEventListener('scroll', updatePosition, true)
        window.removeEventListener('resize', updatePosition)
      }
    }
  }, [displayDropDown, updatePosition])

  const { isMobile } = useWindowSize()

  const preventDropDownFromHide = useRef<boolean>(false)

  const hideDropDown = useCallback(
    (e: MouseEvent | KeyboardEvent): void => {
      if (preventDropDownFromHide.current) {
        preventDropDownFromHide.current = false
        return
      }

      if (e instanceof KeyboardEvent) {
        onKeyDown?.(e, {
          display: displayDropDown,
          show: () => {
            setDisplayDropDown(true)
          },
          hide: () => {
            setDisplayDropDown(false)
            onHide?.()
          },
        })

        if (e.key == 'Escape' && displayDropDown) {
          setDisplayDropDown(false)
          onHide?.()
        }

        return
      }

      const element = e.target as HTMLElement
      if (element && refWrappedComponent.current?.contains(element)) {
        return
      }

      if (element) {
        const elements = document.getElementsByClassName('dropdown-container')
        if (elements) {
          for (let i = 0; i < elements.length; i++) {
            if (elements[i].contains(element)) {
              return
            }
          }
        }
      }

      if (refDropDown.current) {
        const rect = getPosition(refDropDown.current)
        if (rect.top < e.clientY && e.clientY < rect.bottom && rect.left < e.clientX && e.clientX < rect.right) {
          e.preventDefault()
          e.stopImmediatePropagation()
          e.stopPropagation()
          return
        }
      }

      setDisplayDropDown(false)
      onHide?.()
    },
    [displayDropDown, getPosition, onHide, onKeyDown],
  )

  if (!isMobile && displayDropDown) {
    addKeyDownListener({ key: refComponent.current, method: hideDropDown })
    addMouseUpListener({ key: refComponent.current, method: hideDropDown })
  } else {
    removeKeyDownListener({ key: refComponent.current, method: hideDropDown })
    removeMouseUpListener({ key: refComponent.current, method: hideDropDown })
  }

  useEffect(() => {
    return () => {
      /* eslint-disable react-hooks/exhaustive-deps */
      removeKeyDownListener({ key: refComponent.current, method: hideDropDown })
      removeMouseUpListener({ key: refComponent.current, method: hideDropDown })
      /* eslint-enable react-hooks/exhaustive-deps */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const render = useMemo(
    () =>
      isMobile ? (
        <Sheet
          onClose={() => {
            setDisplayDropDown(false)
            onHide?.()
          }}
        >
          {({ close }) => (
            <ContainerDropDown ref={refDropDown} className="dropdown-container">
              {WrappedChildren({
                display: displayDropDown,
                show: () => {
                  setDisplayDropDown(true)
                },
                hide: () => {
                  close(() => {
                    setDisplayDropDown(false)
                    onHide?.()
                  })
                },
              })}
            </ContainerDropDown>
          )}
        </Sheet>
      ) : (
        <Portal style={dropdownStyle}>
          <ContainerDropDown
            ref={refDropDown}
            className="dropdown-container"
            onMouseDown={() => {
              preventDropDownFromHide.current = true
            }}
          >
            {WrappedChildren({
              display: displayDropDown,
              show: () => {
                setDisplayDropDown(true)
              },
              hide: () => {
                setDisplayDropDown(false)
                onHide?.()
              },
            })}
          </ContainerDropDown>
        </Portal>
      ),
    [isMobile, dropdownStyle, WrappedChildren, displayDropDown, onHide],
  )

  return (
    <>
      <ContainerWrappedComponent
        ref={refWrappedComponent}
        style={containerWrappedComponentStyle}
        onContextMenu={disabled ? undefined : contextMenu ? () => setDisplayDropDown(!displayDropDown) : undefined}
        onClick={disabled ? undefined : contextMenu ? undefined : () => setDisplayDropDown(!displayDropDown)}
      >
        {WrappedComponent({
          display: displayDropDown,
          show: () => {
            setDisplayDropDown(true)
          },
          hide: () => {
            setDisplayDropDown(false)
            onHide?.()
          },
        })}
      </ContainerWrappedComponent>
      {displayDropDown && render}
    </>
  )
}
